import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    MenuItem,
    Alert,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Warehouse as WarehouseIcon,
    LocalShipping as DistributionIcon,
    Factory as ProcessingIcon,
    AcUnit as BlastFreezerIcon
} from '@mui/icons-material';

const FACILITY_TYPES = [
    {
        value: 'cold_storage_warehouse',
        label: 'Cold Storage Warehouse',
        icon: <WarehouseIcon />,
        defaultTemp: -20,
        description: 'Large-scale frozen/refrigerated storage facility',
        fields: {
            sqft: { label: 'Total Square Footage', default: 50000, min: 1000, max: 1000000 },
            ceiling_height: { label: 'Ceiling Height (ft)', default: 30, min: 10, max: 60 },
            loading_docks: { label: 'Number of Loading Docks', default: 6, min: 1, max: 50 },
            insulation_r_value: { label: 'Wall Insulation R-Value', default: 30, min: 10, max: 60 },
            roof_r_value: { label: 'Roof R-Value', default: 40, min: 15, max: 80 }
        },
        defaultCompressor: {
            cooling_capacity_kw: 200,
            electrical_power_kw: 60,
            cop: 3.33,
            min_runtime_hours: 0.25,
            max_runtime_hours: 23,
            min_off_time_hours: 0.25,
            min_power_fraction: 0.3,
        },
    },
    {
        value: 'distribution_center',
        label: 'Distribution Center',
        icon: <DistributionIcon />,
        defaultTemp: 36,
        description: 'Temperature-controlled distribution facility',
        fields: {
            sqft: { label: 'Total Square Footage', default: 100000, min: 5000, max: 2000000 },
            ceiling_height: { label: 'Ceiling Height (ft)', default: 35, min: 12, max: 50 },
            loading_docks: { label: 'Number of Loading Docks', default: 20, min: 2, max: 100 },
            insulation_r_value: { label: 'Wall Insulation R-Value', default: 25, min: 10, max: 50 },
            roof_r_value: { label: 'Roof R-Value', default: 35, min: 15, max: 70 }
        },
        defaultCompressor: {
            cooling_capacity_kw: 100,
            electrical_power_kw: 30,
            cop: 3.33,
            min_runtime_hours: 0.25,
            max_runtime_hours: 23,
            min_off_time_hours: 0.25,
            min_power_fraction: 0.3,
        },
    },
    {
        value: 'food_processing',
        label: 'Food Processing Plant',
        icon: <ProcessingIcon />,
        defaultTemp: 40,
        description: 'Food processing with refrigeration zones',
        fields: {
            sqft: { label: 'Total Square Footage', default: 75000, min: 5000, max: 500000 },
            ceiling_height: { label: 'Ceiling Height (ft)', default: 25, min: 10, max: 40 },
            process_zones: { label: 'Number of Process Zones', default: 4, min: 1, max: 20 },
            insulation_r_value: { label: 'Wall Insulation R-Value', default: 28, min: 10, max: 50 },
            roof_r_value: { label: 'Roof R-Value', default: 38, min: 15, max: 70 }
        },
        defaultCompressor: {
            cooling_capacity_kw: 75,
            electrical_power_kw: 25,
            cop: 3.0,
            min_runtime_hours: 0.25,
            max_runtime_hours: 23,
            min_off_time_hours: 0.25,
            min_power_fraction: 0.3,
        },
    },
    {
        value: 'blast_freezer',
        label: 'Blast Freezer Facility',
        icon: <BlastFreezerIcon />,
        defaultTemp: -40,
        description: 'Rapid freezing facility',
        fields: {
            sqft: { label: 'Total Square Footage', default: 25000, min: 1000, max: 200000 },
            ceiling_height: { label: 'Ceiling Height (ft)', default: 20, min: 10, max: 40 },
            blast_rooms: { label: 'Number of Blast Rooms', default: 3, min: 1, max: 15 },
            insulation_r_value: { label: 'Wall Insulation R-Value', default: 40, min: 20, max: 80 },
            roof_r_value: { label: 'Roof R-Value', default: 50, min: 25, max: 100 }
        },
        defaultCompressor: {
            cooling_capacity_kw: 500,
            electrical_power_kw: 200,
            cop: 2.5,
            min_runtime_hours: 0.25,
            max_runtime_hours: 23,
            min_off_time_hours: 0.5,
            min_power_fraction: 0.3,
        },
    }
];

/**
 * Derive thermal R/C parameters from building specs.
 * These feed directly into the backend's RC thermal model.
 *
 * IMPORTANT: C represents the EFFECTIVE short-term thermal mass that
 * participates in daily temperature cycles, not the total product mass.
 * Only the surface layer of stored product exchanges heat on a 24h timescale
 * (thermal penetration depth ~6" in frozen goods). This is roughly 3-5%
 * of total product mass, plus structural mass (concrete, racking).
 *
 * R represents TOTAL equivalent thermal resistance including envelope,
 * infiltration (door openings), and internal gains (lights, forklifts).
 * This is lower than envelope-only R-value.
 */
function buildThermalParams(facility) {
    const area_sqft = facility.sqft || 50000;
    const r_value = facility.insulation_r_value || 30;
    const ceiling_ft = facility.ceiling_height || 30;
    const volume_cuft = area_sqft * ceiling_ft;

    // Surface area for a rough box (floor + ceiling + 4 walls)
    const side = Math.sqrt(area_sqft);
    const surface_area_sqft = 2 * area_sqft + 4 * side * ceiling_ft;

    // R = r_value / (surface_area / 1000) — units: °F·hr/kBTU
    // Represents total thermal resistance (envelope + infiltration + internal gains)
    // Clamp to [0.2, 2.0] — lower than envelope-only because of door openings etc.
    const r_raw = r_value / (surface_area_sqft / 1000);
    const thermal_resistance_r = Math.max(0.2, Math.min(2.0, r_raw));

    // C = effective short-term thermal mass (kBTU/°F)
    // Only ~5% of total product mass participates in daily cycles
    // (thermal penetration in frozen goods ~6"/day)
    // Effective: volume * fill(50%) * density(50 lb/cuft) * Cp(0.5) * penetration(5%) / 1000
    // Simplified: C ≈ volume * 0.000625 (kBTU/°F), clamped to [50, 500]
    const c_raw = volume_cuft * 0.000625;
    const thermal_capacitance_c = Math.max(50, Math.min(500, c_raw));

    return {
        thermal_resistance_r: Math.round(thermal_resistance_r * 1000) / 1000,
        thermal_capacitance_c: Math.round(thermal_capacitance_c),
    };
}

/**
 * ASHRAE-based compressor auto-sizing from building envelope + peak ambient.
 *
 * Calculates required cooling capacity from the steady-state heat gain
 * through the envelope at the design ambient temperature, plus a safety
 * factor for infiltration, product loads, and equipment aging.
 *
 * Falls back to ASHRAE 0.4% design day default (95°F) when no weather data.
 */
function autoSizeCompressor(facility, facilityType, peakAmbientF) {
    const thermalParams = buildThermalParams(facility);
    const R = thermalParams.thermal_resistance_r;
    const COP = facilityType.defaultCompressor.cop;
    const target = facility.target_temp ?? facilityType.defaultTemp;
    const tempWarmestAllowed = target + 5; // ±5°F deadband

    const designAmbient = peakAmbientF ?? 95; // ASHRAE 0.4% default
    const deltaT = designAmbient - tempWarmestAllowed;
    if (deltaT <= 0) return { ...facilityType.defaultCompressor };

    const KW_TO_KBTU_HR = 3.412;
    // Envelope conduction is ~30% of total cold storage load.
    // Real facilities also have infiltration (door openings ~20%),
    // product cooling (~25%), internal gains (forklifts/lights ~10%),
    // and defrost cycles (~10%). Factor of 3.0 aligns with industry
    // benchmarks of ~25 kWh/sqft/year for cold storage.
    const SAFETY_FACTOR = 3.0;

    const Q_required_kbtu_hr = (deltaT / R) * SAFETY_FACTOR;
    const cooling_capacity_kw = Math.max(10, Math.round(Q_required_kbtu_hr / KW_TO_KBTU_HR));
    const electrical_power_kw = Math.max(5, Math.round(cooling_capacity_kw / COP));

    return {
        cooling_capacity_kw,
        electrical_power_kw,
        cop: COP,
        min_runtime_hours: facilityType.defaultCompressor.min_runtime_hours,
        max_runtime_hours: facilityType.defaultCompressor.max_runtime_hours,
        min_off_time_hours: facilityType.defaultCompressor.min_off_time_hours,
        min_power_fraction: facilityType.defaultCompressor.min_power_fraction,
    };
}

/**
 * ZoneEditor — Configure cold storage facilities with building specs + compressor config.
 *
 * The editor collects user-friendly building parameters (sqft, insulation R-value, etc.)
 * and derives the thermal R/C parameters needed by the backend solver.
 * Compressor is auto-sized from ASHRAE heat-load calculation when building specs change.
 */
export default function ZoneEditor({ zones = [], onChange, peakAmbientF }) {
    const [selectedType, setSelectedType] = useState('');

    const handleAddFacility = () => {
        if (!selectedType) return;

        const facilityType = FACILITY_TYPES.find(t => t.value === selectedType);
        const buildingFields = Object.keys(facilityType.fields).reduce((acc, key) => {
            acc[key] = facilityType.fields[key].default;
            return acc;
        }, {});

        // Derive thermal params from defaults
        const thermalParams = buildThermalParams(buildingFields);

        // Temperature deadband: target ± 5°F
        // In Fahrenheit for cold storage: temp_min (warmest) > temp_max (coldest)
        const target = facilityType.defaultTemp;
        const temp_min = target + 5;  // warmest allowed
        const temp_max = target - 5;  // coldest allowed

        // Auto-size compressor from building envelope + peak ambient
        const compressor = autoSizeCompressor(
            { ...buildingFields, target_temp: target },
            facilityType,
            peakAmbientF
        );

        const newFacility = {
            id: `facility_${Date.now()}`,
            name: `${facilityType.label} ${zones.length + 1}`,
            type: selectedType,
            target_temp: target,
            temp_min,
            temp_max,
            ...buildingFields,
            ...thermalParams,
            compressor,
        };

        onChange([...zones, newFacility]);
        setSelectedType('');
    };

    const handleUpdateFacility = (index, field, value) => {
        const updated = zones.map((facility, i) => {
            if (i !== index) return facility;

            const updatedFacility = { ...facility, [field]: parseFloat(value) || value };

            // Re-derive thermal params + auto-size compressor when building specs change
            const specFields = ['sqft', 'ceiling_height', 'insulation_r_value', 'roof_r_value'];
            if (specFields.includes(field)) {
                const thermalParams = buildThermalParams(updatedFacility);
                const facilityType = FACILITY_TYPES.find(t => t.value === updatedFacility.type);
                const compressor = facilityType
                    ? autoSizeCompressor(updatedFacility, facilityType, peakAmbientF)
                    : updatedFacility.compressor;
                return { ...updatedFacility, ...thermalParams, compressor };
            }

            // Update temp deadband + re-size compressor when target changes
            if (field === 'target_temp') {
                const target = parseFloat(value);
                if (!isNaN(target)) {
                    updatedFacility.temp_min = target + 5;
                    updatedFacility.temp_max = target - 5;
                    const facilityType = FACILITY_TYPES.find(t => t.value === updatedFacility.type);
                    if (facilityType) {
                        updatedFacility.compressor = autoSizeCompressor(updatedFacility, facilityType, peakAmbientF);
                    }
                }
            }

            return updatedFacility;
        });
        onChange(updated);
    };

    const handleUpdateCompressor = (index, field, value) => {
        const updated = zones.map((facility, i) => {
            if (i !== index) return facility;
            return {
                ...facility,
                compressor: {
                    ...facility.compressor,
                    [field]: parseFloat(value) || 0,
                },
            };
        });
        onChange(updated);
    };

    const handleRemoveFacility = (index) => {
        onChange(zones.filter((_, i) => i !== index));
    };

    return (
        <Box>
            {/* Add Facility Selector */}
            <Card sx={{ mb: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderColor: 'rgba(33, 150, 243, 0.3)' }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, color: '#2196F3' }}>
                        Add Facility
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 320 }}>
                            <InputLabel sx={{ color: '#2196F3' }}>Facility Type</InputLabel>
                            <Select
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                                label="Facility Type"
                                sx={{
                                    bgcolor: 'rgba(33, 150, 243, 0.05)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(33, 150, 243, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2196F3',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#2196F3',
                                        borderWidth: 2,
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            minWidth: 600,
                                            maxWidth: 800,
                                        }
                                    }
                                }}
                            >
                                {FACILITY_TYPES.map(type => (
                                    <MenuItem
                                        key={type.value}
                                        value={type.value}
                                        sx={{ py: 1.5 }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <Box sx={{ color: '#2196F3', fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>
                                                {type.icon}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
                                                    {type.label}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                                    {type.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={handleAddFacility}
                            disabled={!selectedType}
                            sx={{
                                background: 'linear-gradient(135deg, #2196F3 0%, #42a5f5 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1976d2 0%, #2196F3 100%)',
                                }
                            }}
                        >
                            Add Facility
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Facility List */}
            {zones.length === 0 ? (
                <Alert severity="info">
                    Select a facility type above to configure your cold storage facility. Weather data will be automatically loaded from the project's location.
                </Alert>
            ) : (
                zones.map((facility, index) => {
                    const facilityType = FACILITY_TYPES.find(t => t.value === facility.type);
                    if (!facilityType) return null;

                    const Icon = facilityType.icon.type;

                    return (
                        <Card key={facility.id} sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Icon sx={{ color: '#2196F3', fontSize: '2rem', mr: 2 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h5" sx={{ color: '#2196F3' }}>
                                            {facility.name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {facilityType.label}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveFacility(index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* Facility Name */}
                                    <TextField
                                        label="Facility Name"
                                        value={facility.name}
                                        onChange={(e) => handleUpdateFacility(index, 'name', e.target.value)}
                                        sx={{ maxWidth: 600 }}
                                    />

                                    {/* Target Temperature */}
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <TextField
                                            type="number"
                                            label="Target Temperature"
                                            value={facility.target_temp}
                                            onChange={(e) => handleUpdateFacility(index, 'target_temp', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">°F</InputAdornment>
                                            }}
                                            helperText="Desired storage temperature"
                                            sx={{ maxWidth: 200 }}
                                        />
                                        <TextField
                                            type="number"
                                            label="Max Temp (warmest)"
                                            value={facility.temp_min}
                                            onChange={(e) => handleUpdateFacility(index, 'temp_min', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">°F</InputAdornment>
                                            }}
                                            helperText="Upper safety limit"
                                            sx={{ maxWidth: 200 }}
                                        />
                                        <TextField
                                            type="number"
                                            label="Min Temp (coldest)"
                                            value={facility.temp_max}
                                            onChange={(e) => handleUpdateFacility(index, 'temp_max', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">°F</InputAdornment>
                                            }}
                                            helperText="Lower limit"
                                            sx={{ maxWidth: 200 }}
                                        />
                                    </Box>

                                    <Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                                            Facility Specifications
                                        </Typography>
                                    </Box>

                                    {/* Dynamic Fields Based on Facility Type */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        {Object.keys(facilityType.fields).map(fieldKey => {
                                            const fieldConfig = facilityType.fields[fieldKey];
                                            return (
                                                <TextField
                                                    key={fieldKey}
                                                    type="number"
                                                    label={fieldConfig.label}
                                                    value={facility[fieldKey] || fieldConfig.default}
                                                    onChange={(e) => handleUpdateFacility(index, fieldKey, e.target.value)}
                                                    inputProps={{
                                                        min: fieldConfig.min,
                                                        max: fieldConfig.max
                                                    }}
                                                    sx={{ minWidth: 250, flex: '1 1 45%' }}
                                                />
                                            );
                                        })}
                                    </Box>

                                    {/* Derived Thermal Parameters (read-only display) */}
                                    <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 1 }}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                            Derived Thermal Parameters (auto-calculated from building specs)
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 3 }}>
                                            <Typography variant="body2">
                                                R = {facility.thermal_resistance_r?.toFixed(3) || '—'} °F·hr/kBTU
                                            </Typography>
                                            <Typography variant="body2">
                                                C = {facility.thermal_capacitance_c?.toFixed(2) || '—'} kBTU/°F
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Compressor Configuration */}
                                    <Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                                            Compressor
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        <TextField
                                            type="number"
                                            label="Cooling Capacity"
                                            value={facility.compressor?.cooling_capacity_kw || 0}
                                            onChange={(e) => handleUpdateCompressor(index, 'cooling_capacity_kw', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">kW<sub>th</sub></InputAdornment>
                                            }}
                                            helperText="Max cooling power (thermal)"
                                            sx={{ minWidth: 200, flex: '1 1 30%' }}
                                        />
                                        <TextField
                                            type="number"
                                            label="Electrical Power"
                                            value={facility.compressor?.electrical_power_kw || 0}
                                            onChange={(e) => handleUpdateCompressor(index, 'electrical_power_kw', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">kW</InputAdornment>
                                            }}
                                            helperText="Electrical consumption when running"
                                            sx={{ minWidth: 200, flex: '1 1 30%' }}
                                        />
                                        <TextField
                                            type="number"
                                            label="COP"
                                            value={facility.compressor?.cop || 0}
                                            onChange={(e) => handleUpdateCompressor(index, 'cop', e.target.value)}
                                            helperText="Coefficient of performance"
                                            sx={{ minWidth: 120, flex: '1 1 20%' }}
                                            inputProps={{ step: 0.1 }}
                                        />
                                        <TextField
                                            type="number"
                                            label="VFD Min Power"
                                            value={facility.compressor?.min_power_fraction ?? 0.3}
                                            onChange={(e) => handleUpdateCompressor(index, 'min_power_fraction', e.target.value)}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">×</InputAdornment>
                                            }}
                                            helperText="Min power fraction when running (0-1)"
                                            sx={{ minWidth: 160, flex: '1 1 20%' }}
                                            inputProps={{ step: 0.05, min: 0, max: 1 }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })
            )}
        </Box>
    );
}
